import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import type { PaymentProvider, ProviderLimits } from "../lib/types";

// Conservative fallback shown only if public_provider_limits somehow fails
// to load (e.g. transient network error) - kept deliberately identical to
// the DB defaults in supabase/migrations/20260101000004_premium_and_config.sql
// so the UI still behaves sensibly rather than accepting any amount.
const FALLBACK: ProviderLimits[] = [
  { provider: "stripe", minAmountCents: 50, maxAmountCents: 1_000_000, enabled: true },
  { provider: "paypal", minAmountCents: 300, maxAmountCents: 1_000_000, enabled: true },
  { provider: "buymeacoffee", minAmountCents: 300, maxAmountCents: 1_000_000, enabled: true },
];

interface ProviderLimitsRow {
  provider: PaymentProvider;
  min_amount_cents: number;
  max_amount_cents: number;
  enabled: boolean;
}

/**
 * Reads per-provider purchase limits (via the public_provider_limits view)
 * once on mount, so the purchase panel can offer only enabled providers and
 * validate the amount against each provider's own min/max before even
 * calling its create-* Edge Function - see README, "Minimalni znesek
 * nakupa po ponudniku".
 */
export function usePaymentProviders(): { providers: Map<PaymentProvider, ProviderLimits>; loading: boolean } {
  const [providers, setProviders] = useState<Map<PaymentProvider, ProviderLimits>>(
    new Map(FALLBACK.map((p) => [p.provider, p])),
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error } = await supabase
        .from("public_provider_limits")
        .select("provider, min_amount_cents, max_amount_cents, enabled");
      if (!active) return;
      if (error) {
        console.error("failed to load public_provider_limits", error);
      } else if (data) {
        const map = new Map<PaymentProvider, ProviderLimits>(
          (data as ProviderLimitsRow[]).map((row) => [
            row.provider,
            {
              provider: row.provider,
              minAmountCents: Number(row.min_amount_cents),
              maxAmountCents: Number(row.max_amount_cents),
              enabled: row.enabled,
            },
          ]),
        );
        setProviders(map);
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  return { providers, loading };
}
